/**
 * boardWorkspace — the Project Wide board.
 *
 * The board is a HIDDEN workspace per project/org (`project_board_<orgId>`):
 * an infinite canvas where documents pulled from ANY of the org's workspaces
 * sit side by side for review and markup. It reuses the entire MainView stack
 * (quick-view chrome, infinite view, the full markup toolset) rather than a
 * separate canvas — a fourth markup surface would drift the way the three
 * views used to.
 *
 * Adding a document takes a SNAPSHOT COPY of the PDF into the board's own
 * storage (POST /api/pdf/copy). That one decision is what keeps the board
 * safe: markup saves — including save-in-place, which overwrites the PDF
 * bytes — write the board's copy and can never touch the source workspace's
 * document. No SharePoint checkout, no cross-workspace locks. The Bluebeam
 * Sessions model.
 *
 * Board/quick ids are "system" projects: hidden from every workspace list,
 * switcher, recents feed and search surface.
 */

import { getProject, saveProject } from './storage';
import { apiFetch } from './apiFetch';
import { downloadFile as spCacheDownload } from './sharepoint';

export const BOARD_PREFIX = 'project_board_';
export const QUICK_PREFIX = 'project_quick_';

export const boardProjectId = (orgId) => `${BOARD_PREFIX}${orgId}`;
export const isBoardViewId = (id) => typeof id === 'string' && id.startsWith(BOARD_PREFIX);
export const isQuickViewId = (id) => typeof id === 'string' && id.startsWith(QUICK_PREFIX);
/** Quick View OR board: render the lean (no-entity) workspace chrome. */
export const isLeanViewId = (id) => isQuickViewId(id) || isBoardViewId(id);
/** Same set — the projects that must never appear in workspace lists. */
export const isSystemProjectId = isLeanViewId;

/**
 * Get-or-create the org's board workspace; resolves to its project id.
 * The id is deterministic per org, so two users opening it concurrently
 * converge on the same project (the second PUT writes an identical shell).
 */
export async function openOrCreateBoard(orgId) {
  if (!orgId) throw new Error('Project Wide needs a project (org) scope');
  const id = boardProjectId(orgId);
  const existing = await getProject(id).catch(() => null);
  if (existing?.id) return id;
  const now = new Date().toISOString();
  await saveProject({
    id,
    name: 'Project Wide',
    description: 'Cross-workspace canvas — documents pulled from any workspace in this project.',
    client: '',
    tags: [],
    orgId,
    shareWithOrg: true,
    createdAt: now,
    updatedAt: now,
    files: [],
    folders: [],
  });
  return id;
}

/**
 * Snapshot-copy a source workspace's PDF into the board's own storage.
 * Returns the new backendFilename the board file row should reference.
 */
export async function copyDocForBoard({ backendFilename, sourceFolder, projectId, sharepoint }) {
  const attempt = async (fname, folder) => {
    const r = await apiFetch('/api/pdf/copy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: fname,
        sourceFolder: folder || null,
        // For SharePoint docs the backend tries the project's app-only
        // binding on a cache miss.
        projectId: projectId || null,
        sharepoint: sharepoint || null,
      }),
    });
    const d = await r.json().catch(() => ({}));
    return { ok: r.ok && !!d?.filename, filename: d?.filename, error: d?.error };
  };
  let res = await attempt(backendFilename, sourceFolder);
  if (!res.ok && sharepoint?.driveId && sharepoint?.itemId) {
    // Delegated-auth orgs have no app-only binding, so the backend can't
    // fetch the bytes itself. Pull the doc into the backend cache with the
    // BROWSER's own SharePoint session — the same path the viewer uses —
    // then copy the cached file.
    try {
      const dl = await spCacheDownload(sharepoint.driveId, sharepoint.itemId, backendFilename, { silent: true });
      if (dl?.filename) {
        res = await attempt(dl.filename, dl.cacheDir || null);
      }
    } catch { /* fall through to the original error */ }
  }
  if (!res.ok) throw new Error(res.error || 'Could not copy the document');
  return res.filename;
}

/**
 * THE LOCK A DOCUMENT IS HELD BY (2026-11-05): its own SharePoint record,
 * or, for a board row, the record of the SOURCE it was copied from
 * (`boardSource.sharepoint`, overlaid with the lock table by
 * ProjectWorkspace). Every padlock, annotate gate and check-out control
 * asks this rather than `file.isSharePoint`, so a board copy of a
 * SharePoint drawing is as locked as the drawing itself until it is
 * checked out through the board (`onBoardCheckout`: the SOURCE is locked,
 * no bytes move). It used to answer the padlock freely: the board's copy
 * was not a SharePoint document to any gate.
 *   { kind: 'sharepoint' | 'board', checkedOutBy, checkedOutById,
 *     checkedOutByMe, library }  or null for a plain document.
 */
export function lockSourceOf(file) {
  if (!file || typeof file !== 'object') return null;
  if (file.isSharePoint && file.sharepoint) {
    const sp = file.sharepoint;
    return { kind: 'sharepoint', checkedOutBy: sp.checkedOutBy || null, checkedOutById: sp.checkedOutById || null, checkedOutByMe: !!sp.checkedOutByMe, library: !!sp.library };
  }
  const bs = file.boardSource;
  if (bs?.sharepoint?.driveId && bs.sharepoint.itemId) {
    return { kind: 'board', checkedOutBy: bs.checkedOutBy || null, checkedOutById: bs.checkedOutById || null, checkedOutByMe: !!bs.checkedOutByMe, library: !!bs.sharepoint.library };
  }
  return null;
}

/** Locked until checked out: a SharePoint document, or a board copy of
 *  one, that I do not hold. A plain document is never. */
export function lockedUntilCheckout(file) {
  const s = lockSourceOf(file);
  return !!s && !s.checkedOutByMe;
}

/** A board copy of a SharePoint document (its lock is the source's). */
export const isBoardSharePointRow = (file) => lockSourceOf(file)?.kind === 'board';

/**
 * THE BOARD STARTS EMPTY EACH TIME IT IS OPENED (2026-11-05, by the user:
 * it kept its documents across exits and refreshes). The board is a
 * scratch canvas: whatever was placed last time is cleared from the
 * workspace shell before it opens; the copies' markups go with them.
 */
export async function resetBoardDocuments(id) {
  if (!isBoardViewId(id)) return;
  const p = await getProject(id).catch(() => null);
  if (!p?.id) return;
  const hasDocs = (p.files || []).length > 0 || (p.folders || []).some(f => (f?.files || []).length || (f?.subfolders || []).length);
  if (!hasDocs) return;
  await saveProject({ ...p, files: [], folders: [], updatedAt: new Date().toISOString() });
}
